import { supersetRuns, type SectionDraft } from "./builder";

/* 🔴 W96 — THE BUILDER'S DRAG, ONE CONTRACT ON BOTH SURFACES (his report 2026-09-29 ~23:30, his yes ~23:45: *"ok let's go
 * with that. and make sure nothing strange happens in any case. so if we release somewhere random with no position and so
 * on, that it will go back to its first place"*). The gesture itself is `@dnd-kit` (`WorkoutBuilder.tsx`); THIS file is the
 * part of it that is arithmetic, so it is tested without a browser:
 *
 *   · WHAT MOVES is a RUN — a loose exercise, or a superset's whole run — keyed by where it stood when the drag began
 *     (`run:<part>:<first row>`). The draft does not change until the drop, so the key holds for the whole gesture, and
 *     nothing can land between a run's members because a member is never an item.
 *   · WHERE IT GOES is an ARRANGEMENT: each part's list of run keys. Only the carried key ever moves in it; the drop turns
 *     it into ONE call of the one mover (`moveExerciseTo`), so a drag writes nothing the ⋯ menu could not.
 *   · WHEN THE GAP MOVES is `placeAt`: the pointer against each neighbour's MIDPOINT as it is drawn now. A neighbour that
 *     slid out of the way has its midpoint moved by the carried item's height, which is the hysteresis — the pointer that
 *     pushed it has to travel back past the NEW midpoint to pull it back, so nothing oscillates.
 */

/** A run's key: the part it stood in and its first row, at pick-up. */
export function runKey(section: number, start: number): string {
  return `run:${String(section)}:${String(start)}`;
}

/** The library row being flown in, while it is over a part (one at a time). */
export const NEW_KEY = "new";

export function parseRunKey(key: string): { section: number; start: number } | null {
  const m = /^run:(\d+):(\d+)$/.exec(key);
  return m === null ? null : { section: Number(m[1]), start: Number(m[2]) };
}

/** Each part's runs, as keys, in order — the arrangement a drag starts from. */
export function arrangementOf(sections: readonly SectionDraft[]): string[][] {
  return sections.map((sec, si) => supersetRuns(sec.exercises).map((run) => runKey(si, run[0] ?? 0)));
}

/** How many rows a run holds (1 for a loose exercise). */
export function runCount(sections: readonly SectionDraft[], key: string): number {
  const at = parseRunKey(key);
  if (at === null) return 1;
  const run = supersetRuns(sections[at.section]?.exercises ?? []).find((r) => r[0] === at.start);
  return run?.length ?? 1;
}

/** Where a key sits in an arrangement, or null. */
export function locate(arr: readonly (readonly string[])[], key: string): { section: number; index: number } | null {
  for (let s = 0; s < arr.length; s++) {
    const i = arr[s]!.indexOf(key);
    if (i !== -1) return { section: s, index: i };
  }
  return null;
}

/** The arrangement with `key` taken out of wherever it is and put into part `section` at `index` (clamped). A key not
 *  present yet (the library's `new`) is simply inserted. Pure. */
export function moveKey(arr: readonly (readonly string[])[], key: string, section: number, index: number): string[][] {
  const out = arr.map((list) => list.filter((k) => k !== key));
  const target = out[section];
  if (target === undefined) return arr.map((l) => [...l]);
  const at = Math.max(0, Math.min(Math.trunc(index), target.length));
  target.splice(at, 0, key);
  return out;
}

/** The arrangement without `key` (the library row leaving the builder). */
export function withoutKey(arr: readonly (readonly string[])[], key: string): string[][] {
  return arr.map((list) => list.filter((k) => k !== key));
}

/** 🔴 THE MIDPOINT RULE. `mids` are the midpoints (client Y) of the OTHER items in the list, in order, as drawn now —
 *  transforms included. The carried item belongs after every neighbour whose midpoint the pointer has passed. */
export function placeAt(mids: readonly number[], pointerY: number): number {
  let n = 0;
  for (const m of mids) if (m < pointerY) n += 1;
  return n;
}

/** Rows before position `index` of part `section` in the arrangement — the carried key itself never counts. */
function rowsBefore(sections: readonly SectionDraft[], arr: readonly (readonly string[])[], section: number, index: number,
                    carried: string): number {
  let n = 0;
  for (const k of (arr[section] ?? []).slice(0, index)) if (k !== carried && k !== NEW_KEY) n += runCount(sections, k);
  return n;
}

/** 🔴 THE DROP AS ONE CALL OF THE ONE MOVER. The carried run's place in the arrangement, expressed as `moveExerciseTo`'s
 *  arguments — its `to.index` is the SLOT as drawn with the run still in place, because the mover subtracts the span
 *  itself for a move down its own part. Null when the arrangement leaves the run where it started (no write at all). */
export function dropMove(sections: readonly SectionDraft[], arr: readonly (readonly string[])[], key: string):
  { from: { section: number; index: number; count: number }; to: { section: number; index: number } } | null {
  const origin = parseRunKey(key);
  const at = locate(arr, key);
  if (origin === null || at === null) return null;
  const count = runCount(sections, key);
  const before = rowsBefore(sections, arr, at.section, at.index, key);
  if (at.section === origin.section && before === origin.start) return null;
  const slot = at.section === origin.section && before > origin.start ? before + count : before;
  return { from: { section: origin.section, index: origin.start, count }, to: { section: at.section, index: slot } };
}

/** 🔴 W99 — THE ARRANGE PANEL'S FOLDED PARTS AFTER A PART MOVED from `from` to `to`: each index follows its PART. The
 *  fold is kept by index, so without this a moved part left its state behind at its old place and took its new
 *  neighbour's (his live report 2026-09-30: *"when it's closed and dragging and releasing needs to stay closed. now it's
 *  opening"*). `reorderSections` is a remove-then-insert, so this is the same permutation. Pure. */
export function followPartMove(folded: ReadonlySet<number>, from: number, to: number): Set<number> {
  const at = (i: number): number => {
    if (i === from) return to;
    if (from < to && i > from && i <= to) return i - 1;
    if (to < from && i >= to && i < from) return i + 1;
    return i;
  };
  return new Set([...folded].map(at));
}

/** Where the library's new row lands: its part and the row index in that part. Null when it is over no part. */
export function dropInsert(sections: readonly SectionDraft[], arr: readonly (readonly string[])[]):
  { section: number; position: number } | null {
  const at = locate(arr, NEW_KEY);
  if (at === null) return null;
  return { section: at.section, position: rowsBefore(sections, arr, at.section, at.index, NEW_KEY) };
}

/** 🔴 EVERY WAY A DRAG CAN END WITHOUT A POSITION — his list, each one returns the card to its first place and changes
 *  nothing. The page listens for each while a drag is in flight; the tests hold the list and the listeners to each
 *  other, so an ending cannot be named here and forgotten there. */
export const NO_POSITION_ENDINGS = [
  "release outside the list",
  "Escape",
  "the pointer leaving the window",
  "the window losing focus",
  "a scroll mid-drag",
  "a resize mid-drag",
  "a second pointer or touch",
  "a release on a control",
] as const;

/** A release over one of these is "a release on a control" — no position, unless it is inside the carried thing's list
 *  item (a card is full of inputs, and a release over one of them is a release over the card). */
export const CONTROL_SELECTOR = "button, input, textarea, select, a[href], [role='button'], [role='menuitem']";
